import React, { useContext } from 'react';
import { useEffect, useCallback, useState } from 'react';
import PanelPane from './panes/PanelPane';
import SiteRentalPane from './panes/RentalPane';
import PlanningPane from './panes/PlanningPane';
import HealthAndSafetyPane from './panes/HealthAndSafetyPane';
import { ToolbarContext } from '../../contexts/toolbar-context';
import { FileInput } from '../../components/file-input';
import BuildAndMaintenancePane from './panes/BuildAndMaintenance';
import FileSaver from 'file-saver';
import GQLChanges from '../../utils/graphql-changes';
import { fieldMappings } from './fieldMappings';
import { UserContext, useUserContext } from '../../contexts/user-context';
import { BoroughSelect } from "../../components/MaterialSelects/BoroughSelect";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormControl, FormControlLabel,
    Switch,
    TextField
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RegionSelectGrouped } from "../../components/MaterialSelects/RegionSelectGrouped";
import { DistributorSelect } from "../../components/MaterialSelects/DistributorSelect";
import { SubletterSelect } from "../../components/MaterialSelects/SubletterSelect";
import { Map } from "@mui/icons-material";
import { TVAreaSelect } from "../../components/MaterialSelects/TvAreaSelect";
import { SiteTagSelect } from "../../components/MaterialSelects/SiteTagSelect";
import { NumberField } from "../../components/number-input";
import { SiteStructureTypeSelect } from "../../components/MaterialSelects/SiteStructureTypeSelect";
import { SiteStructureSelect } from "../../components/MaterialSelects/SiteStructureSelect";
import { SitePricingSelect } from "../../components/MaterialSelects/SitePricingSelect";
import { StyleSelect } from "../../components/MaterialSelects/StyleSelect";
import { useSnackbar } from "notistack";
import { FixedField } from "../../components/FixedField";
import { ButtonField } from "../../components/ButtonField";
import WaitSpinner from "../../components/wait-spinner";
import { SiteSelect } from '../../components/MaterialSelects/SiteSelect';
import { useSiteReportMutation } from '../../generated/graphql';
import SiteDescriptionPane from './panes/Description';
import Checkbox from '@mui/material/Checkbox';
import DigitalBookingPane from './panes/DigitalBookingPane';
import SitePaymentPane from './panes/PaymentPane';
import { useDaybookContext } from '../../contexts/Daybook/DayBookContext';

export const SiteForm = ({ diaForm }) => {
    const { values, errors, onChange, isDirty, handleSubmit, ready, setValue, updateInitialValues, status } = diaForm;
    const id = values?.id;
    const userContext = useUserContext();
    const daybook = useDaybookContext();
    const readOnly = !userContext.hasPermission('sites.edit');
    const toolbarContext = useContext(ToolbarContext);
    const user = React.useContext(UserContext);
    const [generateReport] = useSiteReportMutation();
    const [isWait, setIsWait] = useState(false);
    const [isDuplicate, setIsDuplicate] = useState(false);
    const baseUrl = '/sites/';
    useEffect(() => {
        if (id) {
            setIsDuplicate(false);
        }
    }, [id]);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const generatePdf = useCallback(async () => {
        if (!values) return;
        if (isWait) return;
        console.log('active', { siteId: values.id });
        enqueueSnackbar('Generating report', { key: 'REPORT_GEN' });
        //const toastKey =
        //            AppToaster.show({message: 'Generating report', intent: 'primary', timeout: 0});
        setIsWait(true);
        try {
            const data = await generateReport({ variables: { siteId: values.id } });
            setIsWait(false);
            closeSnackbar('REPORT_GEN');
            enqueueSnackbar('Rendering file', { key: 'REPORT_RENDER' });
            //            AppToaster.show({message: 'Rendering file', intent: 'success'}, toastKey);
            const res = await fetch(data.data.siteReport.reportData);
            const blobContent = await res.blob();
            FileSaver.saveAs(blobContent, values.name + '.pdf');
            //values.name+'.pdf');
        } catch (e) {
            closeSnackbar('REPORT_RENDER');
            enqueueSnackbar('Fetch failed', { key: 'REPORT_FAILED', variant: 'error' });
            //AppToaster.show({message: 'Fetch failed', intent: 'danger'}, toastKey);
            setIsWait(false);
        }
    }, [values?.id]);
    const createDuplicate = async () => {
        setIsDuplicate(true);
    };
    const isNew = (id === 'new' || isDuplicate);

    const field = {
        onChange: onChange,
        readOnly: readOnly,
        setValue: setValue
    };
    useEffect(() => {
        if (status != 'ready') return;
        const buttons = [];
        if (!readOnly) {
            buttons.push({
                type: 'button',
                icon: 'duplicate',
                tooltip: 'Create Duplicate',
                label: 'Clone Site',
                style: 'secondary',
                callback: createDuplicate,
                className: isDirty ? 'intent-primary' : 'intent-warning',
                disabled: isDirty || isDuplicate
            });
        }
        buttons.push({
            type: 'button',
            icon: 'download',
            tooltip: 'Generate PDF',
            label: 'Generate PDF',
            style: 'secondary',
            callback: generatePdf,
            disabled: isDirty || isNew
        });
        if (!readOnly) {
            buttons.push({
                type: 'button',
                icon: 'save',
                tooltip: 'Save',
                callback: handleSubmit,
                label: 'Save',
                style: 'primary',
                disabled: !isDirty
            });
        }
        toolbarContext.setSecondary(
            {
                primaryUrl: baseUrl,
                title: (!isNew ? (values.site_code ? values.site_code + ' / ' : '') + values.name : 'New Site'),
                secondary: buttons,
                secondaryUrl: id
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirty, id, isDuplicate, status]);

    useEffect(() => {
        if (isDuplicate && !isNaN(values?.id)) {
            const changes = new GQLChanges(fieldMappings);
            const newValues = changes.clone(values);
            newValues.name += ' (copy)';
            delete newValues.three_words;
            updateInitialValues({}, null, true);
            setValue('', newValues);
        }
    }, [isDuplicate, isNew, values?.id]);
    if (status == 'loading') {
        return <WaitSpinner variant="panel" />;
    }
    return (
        <React.Fragment>
            <Accordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Location
                </AccordionSummary>
                <AccordionDetails>
                    <div className="sites__location">
                        <div className="sites__location__left grid12">
                            <TextField label="Site" {...field} className="grid12-12" placeholder="Site name"
                                name="name" value={values.name} size="small"
                                error={!!errors?.name} />

                            <TextField {...field} label="address" className="grid12-10" placeholder="Address 1"
                                name="address1" value={values.address1} size="small"
                                error={!!errors?.address1} />
                            <TextField {...field} className="grid12-10" placeholder="Address 2" name="address2"
                                value={values.address2} size="small"
                                error={!!errors?.address2} />
                            <TextField {...field} className="grid12-10" placeholder="Postcode" name="postcode"
                                value={values.postcode} size="small"
                                error={!!errors?.postcode} />
                            <RegionSelectGrouped {...field} name="region" value={values.region}
                                error={!!errors?.region} label="Region" className="grid12-10" />
                            <BoroughSelect label="Borough" {...field} className="grid12-10"
                                name="borough" value={values.borough}
                                error={!!errors?.borough} />
                            <TVAreaSelect {...field} value={values.tvArea} name="tvArea"
                                error={errors?.tvArea ? 'danger' : null} label="TV Area"
                                className="grid12-10" />
                            <TextField {...field} label="Latitude" className="grid12-3 grid12-clear" name="latitude"
                                value={values.latitude} placeholder="Lat"
                                error={!!errors?.longitude} size="small" />
                            <TextField {...field} label="Longitude" className="grid12-3" placeholder="Lng"
                                value={values.longitude} name="longitude"
                                error={!!errors?.latitude} size="small" />
                            {
                                (values.latitude != '' && values.longitude != '') &&
                                <ButtonField className="grid12-2" href={'https://www.google.com/maps/search/?api=1&query=' + values.latitude + ',' + values.longitude}
                                    target="_blank"
                                    color="primary"
                                    label={<span>Map</span>} buttonContent={<Map />} />
                            }
                            <FixedField label="What 3 words" className="grid12-2" value={values.three_words} />
                            <TextField multiline={true} {...field} className="grid12-10" name="notes"
                                value={values.notes}
                                label="Notes"
                                error={!!errors?.notes} />
                        </div>
                        <div className="sites__location__right grid12">
                            <DistributorSelect {...field} name="distributor" value={values.distributor}
                                error={!!errors?.distributor} label="Distributor"
                                className="grid12-10" />
                            <SubletterSelect {...field} name="subletter" value={values.subletter}
                                error={!!errors?.subletter} label="Landlord/Sublet from"
                                className="grid12-10" />
                            <TextField {...field} placeholder="Site code" name="site_code"
                                value={values.site_code} label="Site Code"
                                size={"small"} className="grid12-6" />
                            <NumberField {...field} shrink={true} name="impacts" value={values.impacts} className="grid12-6" label="Impacts" size="small" />
                            <SiteSelect {...field} allowBlank={true} name="takeoverSite" value={values.takeoverSite} label="Takeover charge"
                                className="grid12-4" />
                            <TextField {...field} placeholder="Photo Archive URL" name="photo_archive_url"
                                value={values.photo_archive_url} label="Photo Archive URL"
                                size={"small"} className="grid12-12" />
                            <TextField {...field} placeholder="Sharelink POPS URL" name="pops_url"
                                value={values.pops_url} label="POPS URL"
                                size={"small"} className="grid12-12" />

                            <FormControlLabel
                                control={<Switch disabled={readOnly ?? false} {...field} name="is_3p" checked={values.is_3p} color="primary" />}
                                label="Third Party (3P)" className="grid12-12" />
                            <FormControlLabel
                                control={<Switch disabled={readOnly ?? false} {...field} name="is_dashboard_site" checked={values.is_dashboard_site} color="primary" />}
                                label="Launch On Dashboard (4 week period)" className="grid12-12" />
                        </div>
                    </div>
                </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded={true}>
                <AccordionSummary>Description</AccordionSummary>
                <AccordionDetails> <SiteDescriptionPane values={values} readOnly={readOnly}
                    setValue={setValue} onChange={onChange} user={user} />
                </AccordionDetails>
            </Accordion>
            {/*<div className="sites-photos">
                <Accordion defaultExpanded={true}>
                    <AccordionSummary>Photo</AccordionSummary>
                    <AccordionDetails>

                    </AccordionDetails>
                </Accordion>
            </div>*/}
            {
                userContext.hasPermission('sites.view_sensitive_info') &&
                <>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary >Tags</AccordionSummary>
                        <AccordionDetails>
                            <div className="grid12">
                                <SiteTagSelect multiple={true} value={values.tags} name="tags" {...field} label="Tags" className="grid12-span" />
                            </div>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary>Structure</AccordionSummary>
                        <AccordionDetails>
                            <div className="grid12">
                                <SiteStructureTypeSelect {...field} name="siteStructureType"
                                    value={values.siteStructureType}
                                    error={errors?.siteStructureType} label="Structure Type" className="grid12-2" />
                                <SiteStructureSelect {...field} name="siteStructure"
                                    value={values.siteStructure}
                                    error={errors?.siteStructure} label="Structure" className="grid12-2" />
                                <SitePricingSelect {...field} name="sitePricing" value={values.sitePricing}
                                    error={errors?.sitePricing} label="Site Price Rating" className="grid12-2" />
                                <FormControlLabel
                                    control={<Switch disabled={readOnly ?? false} {...field} name="is_illumination" checked={values.is_illumination} color="primary" />}
                                    label="Illuminated?"
                                    labelPlacement="top" className="grid12-1"
                                />
                                <StyleSelect allowBlank={true} label="Styling" isSiteStyle={true} {...field} name="style"
                                    value={values.style} className="grid12-2" />

                                <FormControlLabel
                                    control={<Switch disabled={readOnly ?? false} {...field} name="is_dynamic" checked={values.is_dynamic} color="primary" />}
                                    label="Dynamic?"
                                    labelPlacement="top" className="grid12-1"
                                />

                            </div>
                            <PanelPane readOnly={readOnly} panels={values.panels} setValue={setValue} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary>Site Rental</AccordionSummary>
                        <AccordionDetails> <SiteRentalPane values={values.siteRentals} readOnly={readOnly}
                            setValue={setValue} />
                        </AccordionDetails>
                    </Accordion>
                    {daybook?.theme != 'daybook' && 
                        <Accordion defaultExpanded={true}>
                            <AccordionSummary>Site Payments</AccordionSummary>
                            <AccordionDetails> 
                                <SitePaymentPane values={values.sitePayments} siteRental={values.siteRentals} readOnly={readOnly} setValue={setValue} />
                            </AccordionDetails>
                        </Accordion>
                    }
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary>Build & Repairs</AccordionSummary>
                        <AccordionDetails>
                            <BuildAndMaintenancePane values={values.siteBuildAndRepairs} onChange={onChange} setValue={setValue}
                                readOnly={readOnly} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary>Planning</AccordionSummary>
                        <AccordionDetails>
                            <PlanningPane values={values.sitePlannings} readOnly={readOnly}
                                setValue={setValue} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary>Health & Safety</AccordionSummary>
                        <AccordionDetails>
                            <HealthAndSafetyPane values={values.siteHealthAndSafeties} readOnly={readOnly}
                                setValue={setValue} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary>Site Files</AccordionSummary>
                        <AccordionDetails>
                            <FormControl label="Site Files">
                                <FileInput label="Upload Site Files" maxImageSize={900} multiple={true} name="siteFilesOther"
                                    fileType="FI" showPreview={true} type="file"
                                    value={values.siteFilesOther} {...field}
                                    allowDelete={user.hasPermission('sites.delete_files')} />
                            </FormControl>
                        </AccordionDetails>
                    </Accordion>
                    {(values.is_dynamic && !values.is_3p) &&
                        <Accordion defaultExpanded={true}>
                            <AccordionSummary>Digital Booking Pricing</AccordionSummary>
                            <AccordionDetails>
                                <DigitalBookingPane values={values.siteDigitalBookingPricing} readOnly={readOnly}
                                    setValue={setValue} />
                            </AccordionDetails>
                        </Accordion>
                    }
                </>
            }
        </React.Fragment>
    )
};

